// Team — ใครเข้าถึงแบรนด์นี้ได้บ้าง มองจากฝั่งคนทำงาน ไม่ใช่ฝั่งผู้ดูแลระบบ
//
// รายชื่อชุดเดียวกับแผง "ผู้เข้าถึง" ในหน้า config ของแบรนด์ แต่เดิมอยู่หลัง Setting
// ซึ่งเปิดได้แค่ Admin — ทีมจึงไม่รู้ด้วยซ้ำว่าใครในทีมเห็นแบรนด์นี้บ้าง และทุกครั้งที่รับ
// คนใหม่เข้าโปรเจกต์ต้องรอ Admin มากดให้ หน้านี้ย้ายทั้งสองอย่างมาไว้ในมือทีมเอง
//
// ใครแก้ได้: Admin หรือคนที่ถือสิทธิ์ manage ของแบรนด์นี้ · ที่เหลือเห็นรายชื่ออย่างเดียว
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { useAppSelector } from "@/app/hooks";
import { isAdmin } from "@features/auth/lib/roles";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { fetchBrandMembers, settingsKeys } from "@features/settings/api/settings-api";
import { MemberRoster } from "@features/settings/components/member-roster";
import { apiErrorMessage } from "@shared/api/client";

export function TeamPage({ brand }: { brand: Brand }) {
  const session = useAppSelector((s) => s.session.current);
  const admin = isAdmin(session);
  const meCode = session?.empCode ?? "";

  /**
   * ถามรายชื่อก่อนเพื่อดูว่าตัวเองถือสิทธิ์อะไร
   *
   * ไม่มี endpoint แยกสำหรับ "ฉันแก้ได้ไหม" เพราะคำตอบอยู่ในรายชื่อที่ต้องโหลดอยู่แล้ว
   * MemberRoster ใช้ query key เดียวกัน จึงเป็นการยิงครั้งเดียว ไม่ใช่สองครั้ง
   */
  const members = useQuery({
    queryKey: settingsKeys.brandMembers(brand.id),
    queryFn: () => fetchBrandMembers(brand.id),
    retry: false,
  });

  const mine = members.data?.find((m) => m.empCode === meCode);
  const canManage = admin || mine?.role === "manage";

  return (
    <>
      <div className="page-head">
        <div>
          <div className="breadcrumb">{brand.name} / Team</div>
          <h1>Team</h1>
          <p className="subtitle">
            คนที่เข้าถึง <b>{brand.name}</b> ได้ · <b>ผู้ดูแล (manage)</b>{" "}
            เพิ่มหรือถอดคนอื่นได้จากหน้านี้ ส่วน <b>ผู้ดู (viewer)</b> ดูรายชื่อได้อย่างเดียว
          </p>
        </div>
        {admin && (
          <Link className="btn-ghost inline-btn" to={brandCfgLink(brand)}>
            ตั้งค่าแบรนด์
          </Link>
        )}
      </div>

      <div className="panel">
        {members.isError ? (
          <div className="empty-state">
            <h3>ดูรายชื่อทีมของแบรนด์นี้ไม่ได้</h3>
            <p className="muted">{apiErrorMessage(members.error)}</p>
          </div>
        ) : (
          <>
            {!members.isPending && !canManage && (
              <p className="muted small-note">
                คุณเป็น <b>ผู้ดู (viewer)</b> ของแบรนด์นี้ — ให้คนที่เป็นผู้ดูแลหรือ Admin
                เป็นคนเพิ่มหรือถอดคนในทีม
              </p>
            )}
            <MemberRoster
              brandId={brand.id}
              canManage={canManage}
              allowSelfEdit={admin}
              meCode={meCode}
              layout="cards"
            />
          </>
        )}
      </div>
    </>
  );
}
